import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Send, Loader, User, Bot, Users, RefreshCw, Inbox } from 'lucide-react';
import { API, apiFetch } from '../lib/api';

export default function ChatbotPanel() {
  const [tab, setTab] = useState('ai'); // 'ai' | 'messages'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Inner tab strip */}
      <div style={{ display: 'flex', gap: 2, padding: '6px 12px 0', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', flexShrink: 0 }}>
        {[
          { id: 'ai',       icon: <Bot size={11} />,   label: 'AI Assistant'     },
          { id: 'messages', icon: <Inbox size={11} />, label: 'Client Messages'  },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: 'none', background: 'none', borderRadius: '4px 4px 0 0',
              color: tab === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderBottom: tab === t.id ? '2px solid #10b981' : '2px solid transparent'
            }}>{t.icon}{t.label}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        {tab === 'ai'       && <AIAssistantTab />}
        {tab === 'messages' && <ClientMessagingTab />}
      </div>
    </div>
  );
}

// ─── AI Assistant Tab ──────────────────────────────────────────────────────────
function AIAssistantTab() {
  const [history, setHistory] = useState([
    { uid: 'init-0', role: 'assistant', text: 'Hello! I\'m your ERP assistant. Ask me anything about your clients, licenses, modules, or request a SQL query. Try: "How many active licenses?" or "Show Apparel clients".' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history]);

  const send = useCallback(async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    const userMsg = { uid: `u-${Date.now()}`, role: 'user', text: msg };
    const newHistory = [...history, userMsg];
    setHistory(newHistory);
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/admin/smart-assist/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history: newHistory.slice(0, -1) })
      });
      const d = await res.json();
      setHistory(h => [...h, { uid: `a-${Date.now()}`, role: 'assistant', text: d.reply || d.error || '(no response)' }]);
    } catch (e) {
      setHistory(h => [...h, { uid: `err-${Date.now()}`, role: 'assistant', text: `Error: ${e.message}` }]);
    }
    setLoading(false);
  }, [input, history, loading]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {history.map((m) => <ChatBubble key={m.uid} role={m.role} text={m.text} />)}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: 11 }}>
            <Loader size={12} className="spin" /> Thinking…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        <input
          className="form-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask about your ERP data… (Enter to send)"
          data-testid="chatbot-input"
          style={{ flex: 1, fontSize: 12 }}
          disabled={loading}
        />
        <button
          className="erp-btn erp-btn-primary"
          onClick={send}
          disabled={!input.trim() || loading}
          data-testid="chatbot-send"
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 14px', fontSize: 12 }}
        >
          <Send size={12} /> Send
        </button>
      </div>
    </div>
  );
}

function ChatBubble({ role, text }) {
  const isUser = role === 'user';
  // Render code blocks in assistant messages
  const parts = text.split(/(```[\s\S]*?```)/g);
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start',
      flexDirection: isUser ? 'row-reverse' : 'row'
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        background: isUser ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {isUser ? <User size={12} color="#3b82f6" /> : <Bot size={12} color="#10b981" />}
      </div>
      <div style={{
        maxWidth: '80%', padding: '7px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.55,
        background: isUser ? 'rgba(59,130,246,0.10)' : 'var(--surface-2)',
        color: 'var(--text-primary)',
        borderTopRightRadius: isUser ? 2 : 10,
        borderTopLeftRadius: isUser ? 10 : 2,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word'
      }}>
        {parts.map((p, i) => {
          if (p.startsWith('```')) {
            const code = p.replace(/^```[a-z]*\n?/, '').replace(/```$/, '');
            return (
              <pre key={i} style={{
                background: 'var(--surface-3)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '6px 10px', fontSize: 10,
                fontFamily: 'var(--font-mono, monospace)', overflowX: 'auto', margin: '6px 0'
              }}>{code}</pre>
            );
          }
          return <span key={i}>{p}</span>;
        })}
      </div>
    </div>
  );
}

// ─── Client Messaging Tab ──────────────────────────────────────────────────────
function ClientMessagingTab() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const res = await apiFetch(`${API}/admin/messaging/clients`);
      if (res.ok) setClients(await res.json());
    } catch (e) { console.error('[Messaging] Failed to fetch clients:', e.message); }
    setLoadingClients(false);
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const loadMessages = useCallback(async (clientId) => {
    setLoadingMsgs(true);
    try {
      const res = await apiFetch(`${API}/admin/messaging/history?clientId=${clientId}&limit=50`);
      if (res.ok) {
        const data = await res.json();
        setMessages([...data].reverse()); // oldest first for display
      }
    } catch (e) { console.error('[Messaging] Failed to load messages:', e.message); }
    setLoadingMsgs(false);
  }, []);

  const selectClient = (c) => { setSelectedClient(c); loadMessages(c.ClientId); };

  const sendMsg = useCallback(async () => {
    if (!selectedClient || !msgInput.trim() || sending) return;
    const msg = msgInput.trim();
    setMsgInput('');
    setSending(true);
    try {
      const res = await apiFetch(`${API}/admin/messaging/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: selectedClient.ClientId, message: msg })
      });
      if (res.ok) {
        const d = await res.json();
        setMessages(m => [...m, { Direction: 'outbound', Message: msg, SentBy: 'admin', SentAt: d.sentAt || new Date().toISOString(), MsgId: d.msgId }]);
      }
    } catch (e) { console.error('[Messaging] Failed to send message:', e.message); }
  }, [selectedClient, msgInput, sending]);

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Client list */}
      <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Clients</span>
          <button className="erp-btn" onClick={fetchClients} style={{ padding: '2px 5px' }} title="Refresh"><RefreshCw size={10} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingClients && <div style={{ padding: 10, fontSize: 11, color: 'var(--text-secondary)' }}>Loading…</div>}
          {clients.map(c => (
            <button key={c.ClientId} onClick={() => selectClient(c)}
              data-testid={`msg-client-${c.ClientId}`}
              style={{
                display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left',
                padding: '6px 10px', border: 'none', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                background: selectedClient?.ClientId === c.ClientId ? 'rgba(16,185,129,0.1)' : 'transparent',
                color: 'var(--text-primary)', transition: 'background 100ms'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{c.CompanyName}</span>
                {c.UnreadCount > 0 && (
                  <span style={{ background: '#10b981', color: '#fff', borderRadius: 9, padding: '0 5px', fontSize: 9, fontWeight: 700 }}>{c.UnreadCount}</span>
                )}
              </div>
              <span style={{ fontSize: 9, color: 'var(--text-secondary)' }}>{c.ClientId} · {c.Tier}</span>
            </button>
          ))}
          {!loadingClients && !clients.length && (
            <div style={{ padding: 12, fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center' }}>No active clients</div>
          )}
        </div>
      </div>

      {/* Message area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selectedClient ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: 12, flexDirection: 'column', gap: 8 }}>
            <Users size={24} style={{ opacity: 0.3 }} />
            Select a client to view messages
          </div>
        ) : (
          <>
            <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface-3)', flexShrink: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{selectedClient.CompanyName}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{selectedClient.ClientId} · {selectedClient.EmailId}</div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {loadingMsgs && <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 11 }}><Loader size={12} className="spin" /> Loading…</div>}
              {!loadingMsgs && !messages.length && (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12, paddingTop: 20 }}>No messages yet. Start the conversation.</div>
              )}
              {messages.map((m, i) => (
                <div key={m.MsgId || i} style={{
                  alignSelf: m.Direction === 'outbound' ? 'flex-end' : 'flex-start',
                  maxWidth: '75%', padding: '6px 10px', borderRadius: 8, fontSize: 12,
                  background: m.Direction === 'outbound' ? 'rgba(16,185,129,0.12)' : 'var(--surface-2)',
                  color: 'var(--text-primary)', wordBreak: 'break-word',
                  borderBottomRightRadius: m.Direction === 'outbound' ? 2 : 8,
                  borderBottomLeftRadius: m.Direction === 'outbound' ? 8 : 2,
                }}>
                  <div>{m.Message}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-secondary)', marginTop: 2, textAlign: m.Direction === 'outbound' ? 'right' : 'left' }}>
                    {m.SentBy} · {m.SentAt ? new Date(m.SentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Compose */}
            <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <input
                className="form-input"
                value={msgInput}
                onChange={e => setMsgInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
                placeholder="Type a message… (Enter to send)"
                data-testid="messaging-input"
                style={{ flex: 1, fontSize: 12 }}
                disabled={sending}
              />
              <button
                className="erp-btn erp-btn-primary"
                onClick={sendMsg}
                disabled={!msgInput.trim() || sending}
                data-testid="messaging-send"
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 14px', fontSize: 12 }}
              >
                {sending ? <Loader size={11} className="spin" /> : <Send size={11} />}
                Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
